import { selectReferenceGroupTypes } from '@UI/aside/asideReferenceGroup/asideReferenceGroupSelectors';
import type { Content } from '@generalTypes/apiTypes';
import {
  AsyncKovResourcePicker,
  KovResourcePicker,
  Modal,
  useLoadSriOptions,
} from '@kathondvla/react-shared-components/src/components';
import { isString } from 'lodash';
import React, { useState } from 'react';
import { useTypedSelector } from '../../hooks/useTypedSelector';

export interface ReferenceSelectionModalProps {
  onClose?: () => void;
  onSubmit?: (response: { resourceHref: string | null; description: string }) => void;
  selectedResourceHrefs: string[];
}
const DisplayResourceOption = ({ item, selectedType }) => {
  const data = item.$$expanded ?? item;
  if (!selectedType?.display) {
    return data.title;
  }
  if (isString(selectedType.display)) {
    return data[selectedType.display];
  }
  return (
    <span
      dangerouslySetInnerHTML={{
        __html: selectedType.display(data),
      }}
    ></span>
  );
};

const ReferenceSelectionModal: React.FC<ReferenceSelectionModalProps> = ({
  onClose = () => {},
  onSubmit,
  selectedResourceHrefs,
}) => {
  const referenceGroupTypes = useTypedSelector((state) => selectReferenceGroupTypes(state));

  const [selectedType, setSelectedType] = useState<any>(null);
  const [selectedRoot, setSelectedRoot] = useState<Content | null>(null);
  const [selectedResource, setSelectedResource] = useState<Content | null>(null);
  const [resourceHref, setResourceHref] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  const sriLoadOptionsRoot = useLoadSriOptions({
    sriClient: selectedType?.sriClient,
    href: selectedType?.resource,
    parameters: selectedType?.masterResourceFilter,
  });

  const sriLoadOptionsResource = useLoadSriOptions({
    sriClient: selectedType?.sriClient,
    href: selectedType?.resource,
    parameters: {
      ...(selectedRoot && { root: selectedRoot.$$meta.permalink }),
      ...selectedType?.resourceFilter,
    },
  });

  const changeType = (value) => {
    setSelectedType(value);
    setSelectedRoot(null);
    setSelectedResource(null);
  };

  const changeRoot = (value) => {
    setSelectedRoot(value);
    setSelectedResource(null);
  };

  const changeResource = (value) => {
    setSelectedResource(value);
  };

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit({
        resourceHref: selectedResource ? selectedResource.$$meta.permalink : resourceHref,
        description,
      });
      onClose();
    }
  };

  return (
    <div className="overflown-modal">
      <Modal.Header title="Verwijzing toevoegen" onClose={onClose} />
      <Modal.Body>
        <div className="form-group">
          <KovResourcePicker
            value={selectedType}
            localData={referenceGroupTypes}
            clearable={false}
            onChange={changeType}
            menuPlacement="bottom"
            disableSearch={true}
            optionValue={(o) => o.title}
            optionTemplate={(o) => o.title}
            placeholder="Selecteer een type"
          />
          <br />
          {selectedType?.masterResourceFilter && (
            <>
              <AsyncKovResourcePicker
                value={selectedRoot}
                loadOptions={sriLoadOptionsRoot}
                clearable={false}
                disabled={false}
                onChange={(value) => changeRoot(value.$$expanded)}
                placeholder="Zoek een item"
                multiple={false}
                menuPlacement="bottom"
                optionValue={(o) => o.key}
                optionTemplate={(o) => (o.$$expanded ? o.$$expanded.title : o.title)}
              />
              <br />
            </>
          )}
          {selectedType?.resourceFilter && (
            <AsyncKovResourcePicker
              value={selectedResource}
              loadOptions={sriLoadOptionsResource}
              clearable={false}
              disabled={false}
              onChange={(value) => changeResource(value.$$expanded)}
              filter={(value) => !selectedResourceHrefs.includes(value.data.href)}
              placeholder="Zoek een item"
              multiple={false}
              menuPlacement="bottom"
              optionValue={(o) => o.key}
              optionTemplate={(o) => <DisplayResourceOption item={o} selectedType={selectedType} />}
            />
          )}
          {selectedType && !selectedType.resource && (
            <div className="form-group">
              <br />
              <input
                type="text"
                className="form-control"
                onChange={(e) => setResourceHref(e.target.value)}
                value={resourceHref}
              />
            </div>
          )}
          {selectedType && (
            <div className="form-group">
              <br />
              <label className="control-label">Beschrijving</label>
              <textarea
                onChange={(e) => setDescription(e.target.value)}
                className="form-control"
                value={description}
              ></textarea>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-link pull-left" onClick={onClose}>
          Annuleren
        </button>
        <button className="btn btn-primary" onClick={handleSubmit}>
          Toevoegen
        </button>
      </Modal.Footer>
    </div>
  );
};

ReferenceSelectionModal.displayName = 'ReferenceSelectionModal';

export default ReferenceSelectionModal;
